// Shared / Group Voicemail — management page (Phase 2). Lists the tenant's shared
// voicemail boxes and lets an admin create/edit one: name, mailbox number, PIN,
// notification email(s), the five greeting slots (each the SAME AudioPicker the
// personal Voicemail tab uses — stores a custom_prompts prompt_uuid), and a
// member picker (the tenant's extensions) wired to the box's members list.
//
// CRUD/scope skeleton mirrors pages/Voicemail.tsx (getScopedDomain + react-query);
// the member picker mirrors RingGroupEditor's UsersTab select+add pattern. The
// box owns this page + its nav entry only — it does not touch the Ring Group editor.
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Trash2, Plus, Pencil, X, Voicemail as VoicemailIcon } from 'lucide-react';
import { api } from '../lib/api';
import { getScopedDomain } from '../lib/tenantScope';
import AudioPicker from '../components/AudioPicker';
import { GREETING_SLOTS, mailboxError, describeMemberDiff, buildBoxSaveBody } from '../lib/voicemailBox';

type BoxForm = {
  box_uuid?: string;
  name: string;
  mailbox_id: string;
  pin: string;
  email_to: string;
  members: string[];
  mwi_light_enabled: boolean;
  [greetingId: string]: any;
};

const BLANK: BoxForm = { name: '', mailbox_id: '', pin: '', email_to: '', members: [], mwi_light_enabled: false };

export default function SharedVoicemail() {
  const me = api.me();
  const qc = useQueryClient();
  const scopedDomain = getScopedDomain(me?.domain_uuid);
  const [editing, setEditing] = useState<BoxForm | null>(null);
  const [savedMembers, setSavedMembers] = useState<string[]>([]);
  const [err, setErr] = useState<string | null>(null);

  const boxesQ = useQuery({
    queryKey: ['vm-boxes', scopedDomain],
    queryFn: () => (scopedDomain ? api.voicemailBoxes.list(scopedDomain) : Promise.resolve([])),
    enabled: !!scopedDomain,
  });
  const usersQ = useQuery<any[]>({
    queryKey: ['users-list', scopedDomain],
    queryFn: () => api.users.list().catch(() => []),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['vm-boxes', scopedDomain] });

  const save = useMutation({
    mutationFn: async (form: BoxForm) => {
      const body = buildBoxSaveBody(form);
      return form.box_uuid
        ? api.voicemailBoxes.update(form.box_uuid, body)
        : api.voicemailBoxes.create(scopedDomain!, body);
    },
    onSuccess: () => { invalidate(); setEditing(null); setErr(null); },
    onError: (e: any) => setErr(e.message || 'Save failed'),
  });

  const remove = useMutation({
    mutationFn: (boxUuid: string) => api.voicemailBoxes.remove(boxUuid),
    onSuccess: invalidate,
  });

  async function openEdit(boxUuid: string) {
    setErr(null);
    const box = await api.voicemailBoxes.get(boxUuid);
    const members = box.members || [];
    setSavedMembers(members);
    setEditing({
      box_uuid: box.box_uuid,
      name: box.name || '',
      mailbox_id: box.mailbox_id || '',
      pin: box.pin || '',
      email_to: box.email_to || '',
      members,
      mwi_light_enabled: box.mwi_light_enabled === true,
      default_greeting_id: box.default_greeting_id || '',
      vm_greeting_business_id: box.vm_greeting_business_id || '',
      vm_greeting_after_hours_id: box.vm_greeting_after_hours_id || '',
      vm_greeting_break_id: box.vm_greeting_break_id || '',
      vm_greeting_holiday_id: box.vm_greeting_holiday_id || '',
    });
  }

  function openNew() { setErr(null); setSavedMembers([]); setEditing({ ...BLANK, members: [] }); }

  function submit() {
    if (!editing) return;
    const v = mailboxError(editing.name, editing.mailbox_id);
    if (v) { setErr(v); return; }
    save.mutate(editing);
  }

  const boxes = (boxesQ.data as any[]) || [];
  const users = usersQ.data || [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold inline-flex items-center gap-2">
          <VoicemailIcon size={22} /> Shared Voicemail
        </h1>
        {!editing && (
          <button className="btn-primary inline-flex items-center gap-1" onClick={openNew}>
            <Plus size={14} /> New Shared Box
          </button>
        )}
      </div>

      {!editing && (
        <div className="card p-0 overflow-hidden">
          <table className="table">
            <thead>
              <tr><th>Name</th><th>Mailbox</th><th>Email</th><th>Members</th><th></th></tr>
            </thead>
            <tbody>
              {boxes.map((b: any) => (
                <tr key={b.box_uuid} className="hover:bg-elevated">
                  <td className="font-medium">{b.name}</td>
                  <td className="font-mono">{b.mailbox_id}</td>
                  <td className="text-xs">{b.email_to || '—'}</td>
                  <td className="text-xs">{b.enabled === false ? <span className="badge-warn">disabled</span> : <span className="badge-up">enabled</span>}</td>
                  <td className="space-x-1 whitespace-nowrap text-right">
                    <button onClick={() => openEdit(b.box_uuid)} className="btn-ghost p-1" title="Edit"><Pencil size={14} /></button>
                    <button onClick={() => { if (confirm(`Delete shared box "${b.name}"? Its messages are shared and will be removed.`)) remove.mutate(b.box_uuid); }} className="btn-ghost p-1 text-danger" title="Delete"><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!boxes.length && <div className="p-6 text-center text-textSecondary text-sm">No shared voicemail boxes yet. Create one to give a department (front desk, billing…) its own mailbox that several extensions share.</div>}
        </div>
      )}

      {editing && (
        <div className="card p-4 space-y-4">
          <div className="flex items-center justify-between">
            <div className="font-medium">{editing.box_uuid ? `Edit “${editing.name || 'Shared Box'}”` : 'New Shared Voicemail Box'}</div>
            <button className="btn-ghost p-1" onClick={() => { setEditing(null); setErr(null); }} title="Close"><X size={16} /></button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-sm text-textSecondary">Name</span>
              <input className="input w-full" value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })} placeholder="Front Desk" />
            </label>
            <label className="block">
              <span className="text-sm text-textSecondary">Mailbox number</span>
              <input className="input w-full font-mono" value={editing.mailbox_id} onChange={e => setEditing({ ...editing, mailbox_id: e.target.value })} placeholder="7000" />
            </label>
            <label className="block">
              <span className="text-sm text-textSecondary">PIN (optional)</span>
              <input className="input w-full font-mono" value={editing.pin} onChange={e => setEditing({ ...editing, pin: e.target.value })} placeholder="1234" />
            </label>
            <label className="block">
              <span className="text-sm text-textSecondary">Notification email(s), comma-separated</span>
              <input className="input w-full" value={editing.email_to} onChange={e => setEditing({ ...editing, email_to: e.target.value })} placeholder="frontdesk@office.com" />
            </label>
          </div>

          <div className="space-y-2">
            <div className="text-sm font-medium">Greetings</div>
            <div className="text-xs text-textSecondary">Each greeting plays for the matching office-hours state. Leave a state empty to fall back to Default.</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {GREETING_SLOTS.map(slot => (
                <label key={slot.key} className="block">
                  <span className="text-sm text-textSecondary">{slot.label}</span>
                  <AudioPicker value={editing[slot.key] || ''} onChange={(v: string) => setEditing({ ...editing, [slot.key]: v })} placeholder="— No custom greeting —" />
                </label>
              ))}
            </div>
          </div>

          <MemberPicker
            users={users}
            members={editing.members}
            onChange={(members) => setEditing({ ...editing, members })}
          />
          <div className="text-xs text-textSecondary">{describeMemberDiff(savedMembers, editing.members)}</div>

          {/* #1251: light member phones' MWI for the shared box. Adds a second SIP
              account (the box number) to each member's Grandstream phone so its
              message light turns on when the shared box has messages. */}
          <label className="flex items-start gap-2 cursor-pointer pt-2 border-t border-border">
            <input
              type="checkbox"
              className="mt-1"
              checked={!!editing.mwi_light_enabled}
              onChange={e => setEditing({ ...editing, mwi_light_enabled: e.target.checked })}
            />
            <span className="text-sm">
              <span className="font-medium">Light member phones when this box has messages</span>
              <span className="block text-xs text-textSecondary">
                Adds the box as a second line on each member's Grandstream phone so its message
                light turns on for the shared box. Members' phones reprovision (brief reboot).
              </span>
            </span>
          </label>

          {err && <div className="text-sm text-danger">{err}</div>}
          <div className="flex gap-2">
            <button className="btn-primary inline-flex items-center gap-1 disabled:opacity-50" disabled={save.isPending} onClick={submit}>
              {save.isPending ? 'Saving…' : 'Save Box'}
            </button>
            <button className="btn" onClick={() => { setEditing(null); setErr(null); }}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

// Multi-select member picker: single-select + Add (repo convention), chosen
// members listed below with per-row remove. Selection is held in the parent
// form and saved inline with the box (members[] in the create/update body).
function MemberPicker({ users, members, onChange }: { users: any[]; members: string[]; onChange: (m: string[]) => void }) {
  const [add, setAdd] = useState('');
  const chosen = new Set(members.map(String));
  const byUuid = new Map(users.map((u: any) => [String(u.extension_uuid), u]));
  return (
    <div className="space-y-2">
      <div className="text-sm font-medium">Members</div>
      <div className="text-xs text-textSecondary">Every member's phone shares this mailbox — all get message-waiting notification and see/delete the same messages.</div>
      <div className="flex gap-2">
        <select className="input flex-1" value={add} onChange={e => setAdd(e.target.value)}>
          <option value="">— Add a member —</option>
          {users.filter((u: any) => !chosen.has(String(u.extension_uuid))).map((u: any) => (
            <option key={u.extension_uuid} value={u.extension_uuid}>{u.extension} — {u.name}</option>
          ))}
        </select>
        <button className="btn-primary inline-flex items-center gap-1 disabled:opacity-50" disabled={!add} onClick={() => { if (add) { onChange([...members, add]); setAdd(''); } }}>
          <Plus size={14} /> Add
        </button>
      </div>
      {!!members.length && (
        <table className="table">
          <thead><tr><th>Extension</th><th>Name</th><th></th></tr></thead>
          <tbody>
            {members.map((uuid) => {
              const u = byUuid.get(String(uuid));
              return (
                <tr key={uuid}>
                  <td className="font-mono">{u?.extension || uuid}</td>
                  <td>{u?.name || '—'}</td>
                  <td className="text-right"><button onClick={() => onChange(members.filter(m => m !== uuid))} className="btn-ghost p-1 text-danger" title="Remove"><Trash2 size={14} /></button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
